<template>
  <div class="dr-register">
    <!-- Success confirmation -->
    <div v-if="submitted" class="dr-success" role="status" aria-live="polite">
      <div class="dr-success-hero">
        <div class="dr-success-icon"><i class="fas fa-check"></i></div>
        <h2>Registration submitted!</h2>
        <p>Thank you, Dr. {{ submittedName }}. We've received your details.</p>
        <span class="dr-status-pill"><i class="fas fa-hourglass-half"></i> Pending verification</span>
      </div>

      <div class="dr-success-body">
        <div class="dr-success-grid">
          <div>
            <h3 class="dr-success-heading">What happens next</h3>
            <ol class="dr-timeline">
              <li class="done">
                <span class="dr-timeline-dot"><i class="fas fa-check"></i></span>
                <div>
                  <div class="dr-timeline-title">Confirmation email sent <span class="dr-tag done">Done</span></div>
                  <p>Sent to <strong>{{ submittedEmail }}</strong></p>
                </div>
              </li>
              <li class="current">
                <span class="dr-timeline-dot"><i class="fas fa-search"></i></span>
                <div>
                  <div class="dr-timeline-title">Verification by our team <span class="dr-tag current">In progress</span></div>
                  <p>We check your medical registration and clinic details. Usually 1–2 working days.</p>
                </div>
              </li>
              <li>
                <span class="dr-timeline-dot"><i class="fas fa-key"></i></span>
                <div>
                  <div class="dr-timeline-title">Login details by email <span class="dr-tag">Up next</span></div>
                  <p>Once approved, your login email and password arrive at <strong>{{ submittedEmail }}</strong>.</p>
                </div>
              </li>
            </ol>
          </div>

          <div class="dr-summary">
            <h3 class="dr-success-heading">Your submission</h3>
            <dl>
              <div v-for="item in submittedSummary" :key="item.label">
                <dt><i :class="item.icon"></i> {{ item.label }}</dt>
                <dd>{{ item.value }}</dd>
              </div>
            </dl>
          </div>
        </div>

        <p v-if="photoUploadFailed" class="dr-note dr-note-warning">
          <i class="fas fa-exclamation-triangle"></i>
          The profile photo could not be uploaded. You can add it from your profile after signing in.
        </p>
        <p class="dr-note dr-note-info">
          <i class="fas fa-envelope-open-text"></i>
          Don't see our email? Check your spam or promotions folder and add <strong>info@yourdoc.in</strong> to your contacts.
        </p>

        <div class="dr-success-actions">
          <template v-if="isRefereeFlow">
            <button type="button" class="btn btn-primary" @click="resetForm"><i class="fas fa-user-plus me-1"></i> Register another doctor</button>
            <router-link to="/my-referrals" class="btn btn-outline-primary">Go to my referrals</router-link>
          </template>
          <template v-else>
            <router-link to="/" class="btn btn-primary"><i class="fas fa-home me-1"></i> Back to home</router-link>
          </template>
        </div>
        <p class="dr-help">Questions? Write to us at <a href="mailto:info@yourdoc.in">info@yourdoc.in</a></p>
      </div>
    </div>

    <!-- Registration form. v-show (not v-if) keeps the reCAPTCHA widget alive across resets. -->
    <form v-show="!submitted" novalidate @submit.prevent="onSubmit" @input="gaFormStarted" @change="gaFormStarted">
      <header class="dr-header">
        <h2>Join YourDoc as a doctor</h2>
        <p>It takes about 5 minutes. Fields marked <span class="dr-req">*</span> are required.</p>
        <ol class="dr-how">
          <li><span>1</span> Submit your details</li>
          <li><span>2</span> We verify them</li>
          <li><span>3</span> Get your login by email</li>
        </ol>
      </header>

      <!-- 1. Personal details -->
      <section class="dr-card">
        <h3 class="dr-section-title"><span class="dr-section-num">1</span> Personal details</h3>
        <div class="row">
          <div class="col-md-6 dr-field">
            <label for="dr-doctorname">Full name <span class="dr-req">*</span></label>
            <input id="dr-doctorname" type="text" class="form-control" v-model.trim="doctorDetails.doctorname"
              maxlength="100" autocomplete="name" placeholder="e.g. Anita Sharma"
              :class="fieldClass('doctorname')" :aria-invalid="!!errors.doctorname"
              @blur="touch('doctorname')" @input="revalidate('doctorname')" />
            <div v-if="errors.doctorname" class="dr-error"><i class="fas fa-exclamation-circle"></i> {{ errors.doctorname }}</div>
          </div>

          <div class="col-md-6 dr-field">
            <label for="dr-email">Email address <span class="dr-req">*</span></label>
            <input id="dr-email" type="email" class="form-control" v-model.trim="doctorDetails.email"
              maxlength="100" autocomplete="email" placeholder="you@example.com"
              :class="fieldClass('email')" :aria-invalid="!!errors.email"
              @blur="touch('email')" @input="revalidate('email')" />
            <div v-if="errors.email" class="dr-error"><i class="fas fa-exclamation-circle"></i> {{ errors.email }}</div>
          </div>

          <div class="col-md-6 dr-field">
            <label for="dr-contactnumbers">Mobile number <span class="dr-req">*</span></label>
            <vue-tel-input class="form-control dr-tel" :class="fieldClass('contactnumbers')" mode="national"
              :defaultCountry="defaultCountry" :autoFormat="false" :only-countries="onlyCountries"
              :inputOptions="{ id: 'dr-contactnumbers', showDialCode: true, maxlength: 15, placeholder: 'Mobile number' }"
              :dropdownOptions="{ showFlags: true, showDialCodeInSelection: false }" :validCharactersOnly="true"
              @on-input="updatePhone" @blur="touch('contactnumbers')" @keypress="onlyDigits" />
            <div v-if="errors.contactnumbers" class="dr-error"><i class="fas fa-exclamation-circle"></i> {{ errors.contactnumbers }}</div>
          </div>

          <div class="col-6 col-md-3 dr-field">
            <label for="dr-age">Age <span class="dr-req">*</span></label>
            <input id="dr-age" type="text" inputmode="numeric" class="form-control" v-model.trim="doctorDetails.age"
              maxlength="2" placeholder="e.g. 35" :class="fieldClass('age')" :aria-invalid="!!errors.age"
              @keypress="onlyDigits" @blur="touch('age')" @input="revalidate('age')" />
            <div v-if="errors.age" class="dr-error"><i class="fas fa-exclamation-circle"></i> {{ errors.age }}</div>
          </div>

          <div class="col-6 col-md-3 dr-field">
            <label for="dr-gender">Gender <span class="dr-req">*</span></label>
            <select id="dr-gender" class="form-select" v-model="doctorDetails.gender"
              :class="fieldClass('gender')" :aria-invalid="!!errors.gender"
              @blur="touch('gender')" @change="touch('gender')">
              <option value="" disabled>Select</option>
              <option v-for="gender in genders" :key="gender.value" :value="gender.value">{{ gender.label }}</option>
            </select>
            <div v-if="errors.gender" class="dr-error"><i class="fas fa-exclamation-circle"></i> {{ errors.gender }}</div>
          </div>

          <div class="col-md-6 dr-field">
            <label for="dr-languagesspoken">Languages spoken</label>
            <VueMultiselect id="dr-languagesspoken" v-model="doctorDetails.languagesspoken" :options="languageOptions"
              :max="5" :multiple="true" :close-on-select="false" placeholder="Select up to 5 languages"
              label="label" track-by="value" />
          </div>

          <div class="col-md-6 dr-field">
            <label>Profile photo <span class="dr-optional">(optional)</span></label>
            <div class="dr-photo">
              <img :src="profileImageUrl || defaultImageUrl" alt="Profile photo preview" />
              <div>
                <label for="dr-photo" class="btn btn-outline-primary btn-sm dr-photo-btn">
                  <i class="fa fa-upload"></i> {{ uploadedImageFile ? "Change photo" : "Upload photo" }}
                </label>
                <input id="dr-photo" type="file" class="visually-hidden" accept="image/jpeg,image/png" @change="uploadImage" />
                <div v-if="errors.photo" class="dr-error"><i class="fas fa-exclamation-circle"></i> {{ errors.photo }}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 2. Professional details -->
      <section class="dr-card">
        <h3 class="dr-section-title"><span class="dr-section-num">2</span> Professional details</h3>
        <div class="row">
          <div class="col-md-6 dr-field">
            <label for="dr-registration_info">Medical registration number <span class="dr-req">*</span></label>
            <input id="dr-registration_info" type="text" class="form-control" v-model.trim="doctorDetails.registration_info"
              maxlength="50" placeholder="e.g. KMC 123456" :class="fieldClass('registration_info')"
              :aria-invalid="!!errors.registration_info"
              @blur="touch('registration_info')" @input="revalidate('registration_info')" />
            <div v-if="errors.registration_info" class="dr-error"><i class="fas fa-exclamation-circle"></i> {{ errors.registration_info }}</div>
          </div>

          <div class="col-md-6 dr-field">
            <label for="dr-specialization">Specialization <span class="dr-req">*</span></label>
            <select id="dr-specialization" class="form-select" v-model="doctorDetails.specialization"
              :class="fieldClass('specialization')" :aria-invalid="!!errors.specialization"
              @blur="touch('specialization')" @change="touch('specialization')">
              <option value="" disabled>Select your specialization</option>
              <option v-for="speciality in specialities" :key="speciality.id" :value="speciality.Title">{{ speciality.Title }}</option>
            </select>
            <div v-if="errors.specialization" class="dr-error"><i class="fas fa-exclamation-circle"></i> {{ errors.specialization }}</div>
          </div>

          <div class="col-md-6 dr-field">
            <label for="dr-qualifications">Qualifications <span class="dr-req">*</span></label>
            <VueMultiselect id="dr-qualifications" v-model="doctorDetails.qualifications" :options="qualificationOptions"
              :max="4" :multiple="true" :close-on-select="false" placeholder="Select up to 4 qualifications"
              label="label" track-by="value" :class="{ 'dr-ms-invalid': errors.qualifications }"
              @close="touch('qualifications')" @update:modelValue="revalidate('qualifications')" />
            <div v-if="errors.qualifications" class="dr-error"><i class="fas fa-exclamation-circle"></i> {{ errors.qualifications }}</div>
          </div>

          <div class="col-md-6 dr-field">
            <label for="dr-experience">Years of experience <span class="dr-req">*</span></label>
            <input id="dr-experience" type="text" inputmode="numeric" class="form-control" v-model.trim="doctorDetails.experience"
              maxlength="2" placeholder="e.g. 8" :class="fieldClass('experience')" :aria-invalid="!!errors.experience"
              @keypress="onlyDigits" @blur="touch('experience')" @input="revalidate('experience')" />
            <div v-if="errors.experience" class="dr-error"><i class="fas fa-exclamation-circle"></i> {{ errors.experience }}</div>
          </div>
        </div>
      </section>

      <!-- 3. Clinic & location -->
      <section class="dr-card">
        <h3 class="dr-section-title"><span class="dr-section-num">3</span> Clinic &amp; location</h3>
        <div class="row">
          <div class="col-md-6 dr-field">
            <label for="dr-clincorhospname">Clinic / hospital name <span class="dr-req">*</span></label>
            <input id="dr-clincorhospname" type="text" class="form-control" v-model.trim="doctorDetails.clincorhospname"
              maxlength="100" placeholder="Where you see patients" :class="fieldClass('clincorhospname')"
              :aria-invalid="!!errors.clincorhospname"
              @blur="touch('clincorhospname')" @input="revalidate('clincorhospname')" />
            <div v-if="errors.clincorhospname" class="dr-error"><i class="fas fa-exclamation-circle"></i> {{ errors.clincorhospname }}</div>
          </div>

          <div class="col-md-6 dr-field">
            <label for="dr-city">City <span class="dr-req">*</span></label>
            <select id="dr-city" class="form-select" v-model="selectedCityOption" :disabled="citiesLoading"
              :class="fieldClass('city')" :aria-invalid="!!errors.city" @change="onCityOptionChange">
              <option :value="null" disabled>{{ citiesLoading ? "Loading cities..." : "Select your city" }}</option>
              <option :value="otherCityValue">{{ otherCityLabel }} (city not listed)</option>
              <option disabled>──────────</option>
              <option v-for="city in cities" :key="city.city_id" :value="city.city_id">{{ city.name }}</option>
            </select>
            <div v-if="citiesError" class="dr-error"><i class="fas fa-exclamation-circle"></i> {{ citiesError }}</div>
            <div v-if="selectedCity" class="dr-location-chip">
              <i class="fas fa-map-marker-alt"></i>
              {{ [selectedCity.name, selectedCity.state, selectedCity.country].filter(Boolean).join(", ") }}
            </div>
            <div v-if="errors.city && !isManualCity" class="dr-error"><i class="fas fa-exclamation-circle"></i> {{ errors.city }}</div>
          </div>

          <!-- Manual city entry -->
          <div v-if="isManualCity" class="col-12">
            <div class="dr-manual-city">
              <div class="row">
                <div class="col-md-4 dr-field">
                  <label for="dr-cityname">City name <span class="dr-req">*</span></label>
                  <input id="dr-cityname" type="text" class="form-control" v-model="manualCityName"
                    :maxlength="cityNameMaxLength" placeholder="e.g. Udupi" :class="fieldClass('city')"
                    :aria-invalid="!!errors.city" @blur="touch('city')" @input="revalidate('city')" />
                  <div v-if="errors.city" class="dr-error"><i class="fas fa-exclamation-circle"></i> {{ errors.city }}</div>
                </div>
                <div class="col-md-4 dr-field">
                  <label for="dr-manualState">State / province</label>
                  <input id="dr-manualState" type="text" class="form-control" v-model="manualState" maxlength="100"
                    placeholder="e.g. Karnataka" :class="fieldClass('manualState')"
                    @blur="touch('manualState')" @input="revalidate('manualState')" />
                  <div v-if="errors.manualState" class="dr-error"><i class="fas fa-exclamation-circle"></i> {{ errors.manualState }}</div>
                </div>
                <div class="col-md-4 dr-field">
                  <label for="dr-manualCountry">Country <span class="dr-req">*</span></label>
                  <select id="dr-manualCountry" class="form-select" v-model="manualCountry"
                    :class="fieldClass('manualCountry')" @change="onCountryChange">
                    <option v-for="country in countryOptions" :key="country" :value="country">{{ country }}</option>
                  </select>
                  <div v-if="errors.manualCountry" class="dr-error"><i class="fas fa-exclamation-circle"></i> {{ errors.manualCountry }}</div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-8 dr-field">
            <label for="dr-address">Clinic address <span class="dr-req">*</span></label>
            <textarea id="dr-address" class="form-control" rows="3" v-model="doctorDetails.address"
              :maxlength="limits.address" placeholder="Building, street, locality / area"
              :class="fieldClass('address')" :aria-invalid="!!errors.address"
              @blur="touch('address')" @input="revalidate('address')"></textarea>
            <div class="dr-field-footer">
              <div v-if="errors.address" class="dr-error"><i class="fas fa-exclamation-circle"></i> {{ errors.address }}</div>
              <span class="dr-counter">{{ doctorDetails.address.length }}/{{ limits.address }}</span>
            </div>
          </div>

          <div class="col-md-4 dr-field">
            <label for="dr-areacode">Area / postal code ({{ postalCodeName }}) <span class="dr-req">*</span></label>
            <input id="dr-areacode" type="text" class="form-control" v-model.trim="doctorDetails.areacode"
              maxlength="10" :inputmode="isIndia ? 'numeric' : 'text'" :placeholder="isIndia ? 'e.g. 575001' : 'Postal / ZIP code'"
              :class="fieldClass('areacode')" :aria-invalid="!!errors.areacode"
              @blur="touch('areacode')" @input="revalidate('areacode')" />
            <div v-if="errors.areacode" class="dr-error"><i class="fas fa-exclamation-circle"></i> {{ errors.areacode }}</div>
          </div>
        </div>
      </section>

      <!-- 4. About you -->
      <section class="dr-card">
        <h3 class="dr-section-title"><span class="dr-section-num">4</span> About you</h3>
        <div class="dr-field">
          <label for="dr-description">Short biography <span class="dr-req">*</span></label>
          <textarea id="dr-description" class="form-control" rows="4" v-model="doctorDetails.description"
            :maxlength="limits.description"
            placeholder="e.g. Consultant cardiologist with 10 years of experience in managing heart disease and hypertension..."
            :class="fieldClass('description')" :aria-invalid="!!errors.description"
            @blur="touch('description')" @input="revalidate('description')"></textarea>
          <div class="dr-field-footer">
            <div v-if="errors.description" class="dr-error"><i class="fas fa-exclamation-circle"></i> {{ errors.description }}</div>
            <span class="dr-counter">{{ doctorDetails.description.length }}/{{ limits.description }}</span>
          </div>
        </div>

        <!-- Online presence: optional, collapsed by default -->
        <div class="dr-social">
          <button type="button" class="dr-social-toggle" :aria-expanded="showSocial" @click="showSocial = !showSocial">
            <span>
              <i class="fas fa-globe"></i> Online profiles <span class="dr-optional">(optional)</span>
              <span v-if="socialFilledCount" class="badge bg-light text-dark ms-1">{{ socialFilledCount }} added</span>
            </span>
            <i class="fas" :class="showSocial ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </button>
          <div v-show="showSocial" class="row mt-3">
            <div v-for="social in socialFields" :key="social.key" class="col-md-6 dr-field">
              <label :for="`dr-${social.key}`">{{ social.label }}</label>
              <div class="input-group">
                <span class="input-group-text"><i :class="social.icon"></i></span>
                <input :id="`dr-${social.key}`" type="url" class="form-control" v-model.trim="doctorDetails[social.key]"
                  maxlength="100" :placeholder="social.placeholder" :class="fieldClass(social.key)"
                  :aria-invalid="!!errors[social.key]"
                  @blur="normalizeUrl(social.key)" @input="revalidate(social.key)" />
              </div>
              <div v-if="errors[social.key]" class="dr-error"><i class="fas fa-exclamation-circle"></i> {{ errors[social.key] }}</div>
            </div>
          </div>
        </div>
      </section>

      <!-- Submit -->
      <section class="dr-card dr-submit">
        <div v-if="submitAttempted && errorList.length" class="dr-alert dr-alert-danger" role="alert">
          <strong><i class="fas fa-exclamation-circle"></i>
            Please fix {{ errorList.length === 1 ? "1 field" : `${errorList.length} fields` }} before submitting:</strong>
          <ul>
            <li v-for="item in errorList" :key="item.field">
              <a href="#" @click.prevent="focusField(item.field)">{{ item.label }}</a> — {{ item.message }}
            </li>
          </ul>
        </div>
        <div v-if="serverError" class="dr-alert dr-alert-danger" role="alert">
          <i class="fas fa-exclamation-circle"></i> {{ serverError }}
        </div>

        <div id="recaptcha-container" class="dr-recaptcha"></div>

        <button type="submit" class="btn btn-primary btn-lg dr-submit-btn" :disabled="submitting">
          <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          {{ submitting ? "Submitting registration..." : "Submit registration" }}
        </button>
        <p class="dr-hint text-center mt-2 mb-0">
          Your account will be activated after our team verifies your details.
        </p>
      </section>
    </form>
  </div>
</template>

<script>
import axios from "axios";
import specialities from "@/assets/json/specialization_settings_doctor.json";
import VueMultiselect from "vue-multiselect";
import defaultImageUrl from "@/assets/img/doctors/doctor-thumb-01.jpg";
import {
  CITY_NAME_MAX_LENGTH,
  OTHER_CITY_LABEL,
  OTHER_CITY_VALUE,
  cleanCityName,
  findCityByName,
  validateManualCityName
} from "@/services/cities";
import medicalQualifications from "@/assets/json/doctor/MedicalQualifications.json";
import { trackEvent } from "@/analytics";

// Match the doctor table column sizes so nothing is rejected by the database.
const LIMITS = { address: 250, description: 250 };
const MAX_PHOTO_BYTES = 2 * 1024 * 1024;
const DEFAULT_COUNTRY = "India";

// Field order drives the error summary and which field gets focus first.
const FIELD_LABELS = {
  doctorname: "Full name",
  email: "Email address",
  contactnumbers: "Mobile number",
  age: "Age",
  gender: "Gender",
  photo: "Profile photo",
  registration_info: "Medical registration number",
  specialization: "Specialization",
  qualifications: "Qualifications",
  experience: "Years of experience",
  clincorhospname: "Clinic / hospital name",
  city: "City",
  manualState: "State / province",
  manualCountry: "Country",
  address: "Clinic address",
  areacode: "Area / postal code",
  description: "Short biography",
  facebookurl: "Facebook",
  twitterurl: "X (Twitter)",
  instagramurl: "Instagram",
  linkedinurl: "LinkedIn",
  youtubeurl: "YouTube",
  pintresturl: "Pinterest",
  recaptcha: "Verification"
};

const SOCIAL_FIELDS = [
  { key: "linkedinurl", label: "LinkedIn", icon: "fab fa-linkedin-in", placeholder: "https://linkedin.com/in/your-name" },
  { key: "facebookurl", label: "Facebook", icon: "fab fa-facebook-f", placeholder: "https://facebook.com/your-page" },
  { key: "instagramurl", label: "Instagram", icon: "fab fa-instagram", placeholder: "https://instagram.com/your-handle" },
  { key: "twitterurl", label: "X (Twitter)", icon: "fab fa-twitter", placeholder: "https://x.com/your-handle" },
  { key: "youtubeurl", label: "YouTube", icon: "fab fa-youtube", placeholder: "https://youtube.com/@your-channel" },
  { key: "pintresturl", label: "Pinterest", icon: "fab fa-pinterest-p", placeholder: "https://pinterest.com/your-profile" }
];

function emptyDoctorDetails() {
  return {
    doctorname: "",
    gender: "",
    idp_id: "",
    age: "",
    languagesspoken: [],
    specialization: "",
    qualifications: [],
    experience: "",
    contactnumbers: "",
    email: "",
    area: null,
    city_id: null,
    areacode: "",
    address: "",
    registration_info: "",
    clincorhospname: "",
    description: "",
    weburl: "empty",
    appointment_duration: 0,
    refereecode: "",
    facebookurl: "",
    twitterurl: "",
    instagramurl: "",
    linkedinurl: "",
    youtubeurl: "",
    pintresturl: ""
  };
}

// The real password is issued by the API when an admin approves the doctor,
// so the sign-up password is a random value nobody ever sees.
export default {
  props: ["refereeid"],
  components: { VueMultiselect },
  data() {
    return {
      doctorDetails: emptyDoctorDetails(),
      errors: {},
      touched: {},
      submitAttempted: false,
      submitting: false,
      serverError: "",
      submitted: false,
      submittedName: "",
      submittedEmail: "",
      submittedSummary: [],
      photoUploadFailed: false,
      showSocial: false,
      isValidPhone: false,

      selectedCityOption: null,
      manualCityName: "",
      manualState: "",
      manualCountry: DEFAULT_COUNTRY,
      otherCityValue: OTHER_CITY_VALUE,
      otherCityLabel: OTHER_CITY_LABEL,
      cityNameMaxLength: CITY_NAME_MAX_LENGTH,
      countryOptions: [
        "India", "United States", "United Kingdom", "Canada", "Australia",
        "United Arab Emirates", "Bahrain", "Kuwait", "Oman", "Qatar", "Saudi Arabia",
        "Singapore", "Malaysia", "New Zealand"
      ],

      limits: LIMITS,
      specialities,
      qualificationOptions: medicalQualifications.qualifications,
      socialFields: SOCIAL_FIELDS,
      languageOptions: [
        { label: "English", value: "English" },
        { label: "Hindi", value: "Hindi" },
        { label: "Kannada", value: "Kannada" },
        { label: "Tulu", value: "Tulu" },
        { label: "Malayalam", value: "Malayalam" },
        { label: "Konkani", value: "Konkani" },
        { label: "Urdu", value: "Urdu" },
        { label: "Tamil", value: "Tamil" },
        { label: "Telugu", value: "Telugu" }
      ],
      genders: [
        { label: "Male", value: "M" },
        { label: "Female", value: "F" },
        { label: "Other", value: "O" }
      ],
      defaultCountry: "IN",
      onlyCountries: ["IN", "US", "GB", "CA", "AU", "AE", "BH", "KW", "OM", "QA", "SA", "SG", "MY", "NZ"],

      uploadedImageFile: null,
      profileImageUrl: "",
      defaultImageUrl,

      recaptchaSiteKey: "6LebK30qAAAAAC_R7S23jQE4zG4YQ_NqPJbDNGIK",
      recaptchaWidgetId: null,

      // --- analytics funnel state (not used by the form itself) ---
      gaStarted: false,
      gaSubmitAttempts: 0,
      gaCompleted: false,
      gaAbandonSent: false
    };
  },
  computed: {
    cities() {
      return this.$store.getters.getCities;
    },
    citiesLoading() {
      return this.$store.getters.isCitiesLoading;
    },
    citiesError() {
      return this.$store.getters.getCitiesError;
    },
    isManualCity() {
      return this.selectedCityOption === OTHER_CITY_VALUE;
    },
    selectedCity() {
      if (!this.selectedCityOption || this.isManualCity) return null;
      return this.cities.find(c => c.city_id === this.selectedCityOption) || null;
    },
    selectedCountry() {
      if (this.isManualCity) return this.manualCountry;
      return this.selectedCity?.country || DEFAULT_COUNTRY;
    },
    isIndia() {
      return this.selectedCountry === DEFAULT_COUNTRY;
    },
    postalCodeName() {
      if (this.isIndia) return "PIN code";
      return this.selectedCountry === "United States" ? "ZIP code" : "postal code";
    },
    isRefereeFlow() {
      return this.$route.path.startsWith("/referee");
    },
    socialFilledCount() {
      return SOCIAL_FIELDS.filter(s => this.doctorDetails[s.key]).length;
    },
    errorList() {
      return Object.keys(FIELD_LABELS)
        .filter(field => this.errors[field])
        .map(field => ({ field, label: FIELD_LABELS[field], message: this.errors[field] }));
    }
  },
  mounted() {
    trackEvent("doctor_registration_open", { form_location: this.$route.path });
    // A hard close/reload never unmounts the component, so the abandon event
    // needs the pagehide hook as well as beforeUnmount.
    window.addEventListener("pagehide", this.gaReportAbandon);

    this.$store.dispatch("loadCities");
    this.$nextTick(this.renderRecaptcha);
  },
  beforeUnmount() {
    window.removeEventListener("pagehide", this.gaReportAbandon);
    this.gaReportAbandon();
  },
  methods: {
    // ---------------------------------------------------------------
    // Analytics helpers for the registration funnel:
    // open -> start -> submit -> (error | success), abandon if they
    // leave without a success.
    // ---------------------------------------------------------------
    gaFormStarted() {
      if (this.gaStarted) return;
      this.gaStarted = true;
      trackEvent("doctor_registration_start", { form_location: this.$route.path });
    },
    // How much of the form they actually filled in - tells apart "looked and
    // left" from "nearly finished and gave up".
    gaFilledFieldCount() {
      return Object.values(this.doctorDetails).filter(value => {
        if (Array.isArray(value)) return value.length > 0;
        return value !== "" && value !== null && value !== 0 && value !== "empty";
      }).length;
    },
    gaReportAbandon() {
      if (this.gaCompleted || this.gaAbandonSent) return;
      this.gaAbandonSent = true;
      trackEvent("doctor_registration_abandon", {
        form_location: this.$route.path,
        started: this.gaStarted,
        submit_attempts: this.gaSubmitAttempts,
        filled_fields: this.gaFilledFieldCount()
      });
    },

    // ---------------------------------------------------------------
    // Validation. Each rule returns an error message, or "" when valid.
    // Errors show after a field is left (blur) or after a submit attempt,
    // and clear as soon as the value becomes valid.
    // ---------------------------------------------------------------
    ruleFor(field) {
      const d = this.doctorDetails;
      switch (field) {
        case "doctorname": {
          if (!d.doctorname) return "Please enter your full name.";
          if (/^dr\.?\s/i.test(d.doctorname)) return 'Please enter your name without the "Dr." prefix — we add it for you.';
          if (!/^[\p{L}\p{M}][\p{L}\p{M}\s.'-]*$/u.test(d.doctorname)) {
            return "Name can only contain letters, spaces, periods, apostrophes and hyphens.";
          }
          if (d.doctorname.length < 3) return "Name must be at least 3 characters.";
          return "";
        }
        case "email":
          if (!d.email) return "Please enter your email address.";
          if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(d.email)) {
            return "Please enter a valid email address, e.g. name@example.com.";
          }
          return "";
        case "contactnumbers":
          if (!d.contactnumbers) return "Please enter your mobile number.";
          if (!this.isValidPhone || !/^\+\d{8,15}$/.test(d.contactnumbers)) {
            return "This mobile number doesn't look right for the selected country.";
          }
          return "";
        case "age": {
          if (!d.age) return "Please enter your age.";
          const age = Number(d.age);
          if (!/^\d+$/.test(d.age) || age < 21 || age > 90) return "Age must be between 21 and 90.";
          return "";
        }
        case "gender":
          return d.gender ? "" : "Please select your gender.";
        case "registration_info":
          if (!d.registration_info) return "Please enter your medical registration number.";
          if (d.registration_info.length < 3) return "Registration number must be at least 3 characters.";
          if (!/^[A-Za-z0-9\s/.-]+$/.test(d.registration_info)) {
            return "Registration number can only contain letters, numbers, spaces, '/', '-' and '.'.";
          }
          return "";
        case "specialization":
          return d.specialization ? "" : "Please select your specialization.";
        case "qualifications":
          return d.qualifications && d.qualifications.length ? "" : "Please select at least one qualification.";
        case "experience": {
          if (d.experience === "") return "Please enter your years of experience (0 if less than a year).";
          if (!/^\d+$/.test(d.experience)) return "Experience must be a whole number of years.";
          const age = Number(d.age);
          if (age && Number(d.experience) > age - 20) {
            return `Experience looks too high for age ${age}. Please check both values.`;
          }
          return "";
        }
        case "clincorhospname":
          if (!d.clincorhospname) return "Please enter your clinic or hospital name.";
          if (d.clincorhospname.length < 2) return "Clinic name must be at least 2 characters.";
          return "";
        case "city":
          if (!this.selectedCityOption) return "Please select your city, or choose \"Other / Enter manually\".";
          if (this.isManualCity) return validateManualCityName(this.manualCityName);
          return "";
        case "manualState":
          if (this.isManualCity && this.manualState.trim() && !/^[\p{L}\p{M}\s.'-]+$/u.test(this.manualState.trim())) {
            return "State can only contain letters, spaces, periods, apostrophes and hyphens.";
          }
          return "";
        case "manualCountry":
          return this.isManualCity && !this.manualCountry ? "Please select the country." : "";
        case "address": {
          const address = d.address.trim();
          if (!address) return "Please enter your clinic address.";
          if (address.length < 10) return "Please enter the full address (building, street and locality).";
          if (address.length > LIMITS.address) return `Address must not exceed ${LIMITS.address} characters.`;
          return "";
        }
        case "areacode":
          if (!d.areacode) return `Please enter the ${this.postalCodeName} of your clinic.`;
          if (this.isIndia) {
            return /^[1-9]\d{5}$/.test(d.areacode) ? "" : "PIN code must be 6 digits and cannot start with 0, e.g. 575001.";
          }
          return /^[A-Za-z0-9][A-Za-z0-9 -]{1,8}[A-Za-z0-9]$/.test(d.areacode)
            ? "" : "Please enter a valid postal / ZIP code (3–10 letters or numbers).";
        case "description": {
          const description = d.description.trim();
          if (!description) return "Please write a short biography.";
          if (description.length < 30) return `Please write at least 30 characters (${description.length} so far).`;
          if (description.length > LIMITS.description) return `Biography must not exceed ${LIMITS.description} characters.`;
          return "";
        }
        default:
          if (SOCIAL_FIELDS.some(s => s.key === field)) {
            const url = d[field];
            if (!url) return "";
            if (!/^https?:\/\/[^\s/$.?#][^\s]*\.[^\s]+$/i.test(url)) return "Please enter a full web address, e.g. https://example.com/you.";
            if (url.length > 100) return "Link must not exceed 100 characters.";
          }
          return "";
      }
    },
    validateField(field) {
      const message = this.ruleFor(field);
      this.errors[field] = message;
      return !message;
    },
    touch(field) {
      this.touched[field] = true;
      this.validateField(field);
    },
    // While typing, only re-check fields the user has already left or that
    // already show an error, so we don't nag before they finish.
    revalidate(field) {
      if (this.touched[field] || this.errors[field] || this.submitAttempted) {
        this.validateField(field);
      }
      if (field === "age" && (this.touched.experience || this.errors.experience)) {
        this.validateField("experience");
      }
    },
    validateAll() {
      const fields = Object.keys(FIELD_LABELS).filter(f => f !== "photo" && f !== "recaptcha");
      fields.forEach(f => this.validateField(f));
      return !fields.some(f => this.errors[f]);
    },
    fieldClass(field) {
      return { "is-invalid": !!this.errors[field] };
    },
    focusField(field) {
      if (SOCIAL_FIELDS.some(s => s.key === field)) this.showSocial = true;
      const id = field === "city" && this.isManualCity ? "dr-cityname"
        : field === "recaptcha" ? "recaptcha-container"
        : `dr-${field}`;
      this.$nextTick(() => {
        const el = document.getElementById(id);
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        if (el.focus) el.focus({ preventScroll: true });
      });
    },
    focusFirstError() {
      if (this.errorList.length) this.focusField(this.errorList[0].field);
    },

    // ---------------------------------------------------------------
    // Inputs
    // ---------------------------------------------------------------
    onlyDigits(event) {
      if (event.key && event.key.length === 1 && !/\d/.test(event.key)) event.preventDefault();
    },
    updatePhone(phone, phoneObject) {
      this.doctorDetails.contactnumbers = phoneObject?.number || phone || "";
      this.isValidPhone = !!phoneObject?.valid;
      this.revalidate("contactnumbers");
    },
    onCityOptionChange() {
      this.touched.city = true;
      if (!this.isManualCity) {
        this.manualCityName = "";
        this.manualState = "";
        this.manualCountry = DEFAULT_COUNTRY;
        this.errors.manualState = "";
      }
      // Manual entry starts empty, so only flag an error once they type/leave.
      this.errors.city = this.isManualCity ? "" : this.ruleFor("city");
      if (this.doctorDetails.areacode) this.validateField("areacode");
      if (this.isManualCity) this.$nextTick(() => document.getElementById("dr-cityname")?.focus());
    },
    onCountryChange() {
      this.validateField("manualCountry");
      if (this.doctorDetails.areacode) this.validateField("areacode");
    },
    normalizeUrl(field) {
      const value = this.doctorDetails[field];
      if (value && !/^https?:\/\//i.test(value)) this.doctorDetails[field] = `https://${value}`;
      this.touch(field);
    },
    async uploadImage(event) {
      const file = event.target.files[0];
      event.target.value = "";
      if (!file) return;

      if (!["image/jpeg", "image/png", "image/jpg"].includes(file.type)) {
        this.errors.photo = "Only JPG or PNG images are allowed.";
        return;
      }
      if (file.size > MAX_PHOTO_BYTES) {
        this.errors.photo = `This image is ${(file.size / 1024 / 1024).toFixed(1)} MB. Please choose one under 2 MB.`;
        return;
      }
      this.errors.photo = "";
      this.uploadedImageFile = file;
      this.profileImageUrl = await this.convertToBase64(file);
    },
    convertToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
        reader.readAsDataURL(file);
      });
    },

    // ---------------------------------------------------------------
    // reCAPTCHA
    // ---------------------------------------------------------------
    renderRecaptcha(retries = 10) {
      if (this.recaptchaWidgetId !== null) return;
      if (typeof grecaptcha !== "undefined" && grecaptcha.render) {
        this.recaptchaWidgetId = grecaptcha.render("recaptcha-container", {
          sitekey: this.recaptchaSiteKey,
          callback: () => { this.errors.recaptcha = ""; }
        });
      } else if (retries > 0) {
        setTimeout(() => this.renderRecaptcha(retries - 1), 500);
      } else {
        console.error("reCAPTCHA not loaded. Check the script.");
      }
    },
    resetRecaptcha() {
      if (typeof grecaptcha !== "undefined" && this.recaptchaWidgetId !== null) {
        grecaptcha.reset(this.recaptchaWidgetId);
      }
    },

    // ---------------------------------------------------------------
    // Submit
    // ---------------------------------------------------------------
    // Turns the city dropdown (or the manually entered city) into a city_id
    // and the matching `area` name. Sets errors.city and returns false on failure.
    async applySelectedCity() {
      if (!this.isManualCity) {
        this.doctorDetails.city_id = this.selectedCity.city_id;
        this.doctorDetails.area = this.selectedCity.name;
        return true;
      }

      const name = cleanCityName(this.manualCityName);
      // Reuse a city that is already in the list before asking the backend.
      const known = findCityByName(this.cities, name);
      try {
        const city = known || await this.$store.dispatch("resolveCityByName", {
          name,
          state: cleanCityName(this.manualState) || null,
          country: this.manualCountry
        });
        this.doctorDetails.city_id = city.city_id;
        this.doctorDetails.area = city.name;
        return true;
      } catch (error) {
        console.error("Error saving city:", error);
        this.errors.city = error.response?.data?.detail || "We couldn't save this city. Please try again.";
        return false;
      }
    },
    // Shows taken email / mobile number as field errors. Returns true if any.
    showConflictErrors({ email_taken, phone_taken }) {
      if (email_taken) {
        this.errors.email = "A doctor with this email is already registered. If this is you, wait for your approval email.";
      }
      if (phone_taken) {
        this.errors.contactnumbers = "This mobile number is already registered with another doctor.";
      }
      if (!email_taken && !phone_taken) return false;
      trackEvent("doctor_registration_error", {
        reason: "duplicate",
        field: email_taken ? "email" : "contactnumbers",
        attempt: this.gaSubmitAttempts
      });
      this.focusFirstError();
      return true;
    },
    getRegistrationErrorMessage(error) {
      console.error("Doctor registration failed:", error);
      const name = error?.name || error?.code;
      const detail = error?.response?.data?.detail;

      if (name === "UsernameExistsException") {
        return "An account with this email already exists. If you registered earlier, your application may still be under review — you'll get an email once it's approved.";
      }
      if (name === "InvalidParameterException") {
        return "Some details were not accepted. Please check your email address and mobile number.";
      }
      if (typeof detail === "string") return detail;
      if (detail?.message) return detail.message;
      if (Array.isArray(detail) && detail[0]?.msg) return `Please check your details: ${detail[0].msg}`;
      if (!error?.response && (error?.message === "Network Error" || name === "NetworkError")) {
        return "We couldn't reach the server. Please check your internet connection and try again.";
      }
      return "Something went wrong while submitting your registration. Please try again in a moment.";
    },
    async onSubmit() {
      if (this.submitting) return;
      this.gaFormStarted();
      this.gaSubmitAttempts += 1;
      trackEvent("doctor_registration_submit", {
        form_location: this.$route.path,
        attempt: this.gaSubmitAttempts,
        filled_fields: this.gaFilledFieldCount()
      });

      this.submitAttempted = true;
      this.serverError = "";

      const valid = this.validateAll();
      const recaptchaResponse = typeof grecaptcha !== "undefined" && this.recaptchaWidgetId !== null
        ? grecaptcha.getResponse(this.recaptchaWidgetId) : "";
      this.errors.recaptcha = recaptchaResponse ? "" : "Please tick \"I'm not a robot\" to continue.";

      if (!valid || !recaptchaResponse) {
        trackEvent("doctor_registration_error", {
          reason: valid ? "recaptcha_incomplete" : "validation",
          field: this.errorList[0]?.field || "unknown",
          attempt: this.gaSubmitAttempts
        });
        this.focusFirstError();
        return;
      }

      this.submitting = true;
      try {
        if (!(await this.applySelectedCity())) {
          this.focusField("city");
          trackEvent("doctor_registration_error", { reason: "validation", field: "city", attempt: this.gaSubmitAttempts });
          return;
        }

        const d = this.doctorDetails;
        const doctorPayload = {
          ...d,
          address: d.address.trim(),
          description: d.description.trim(),
          email: d.email.toLowerCase(),
          languagesspoken: d.languagesspoken.map(lang => lang.value).join(", "),
          qualifications: d.qualifications.map(q => q.value).join(", ")
        };
        SOCIAL_FIELDS.forEach(({ key }) => { if (!doctorPayload[key]) doctorPayload[key] = null; });

        // Check duplicates first so a taken email or number is shown on the
        // field instead of as a failed submit.
        const { data: availability } = await axios.get("doctors/availability", {
          params: { email: doctorPayload.email, contactnumbers: doctorPayload.contactnumbers }
        });
        if (this.showConflictErrors(availability)) return;

        // The API creates the doctor's login (no password until an admin
        // approves; a login left by an earlier failed attempt is reused).
        doctorPayload.idp_id = null;

        const response = await axios.post("doctors/", doctorPayload);

        // The registration is already saved; a failed photo upload is only a warning.
        this.photoUploadFailed = false;
        if (this.uploadedImageFile) {
          try {
            const formData = new FormData();
            formData.append("image_file", this.uploadedImageFile);
            await axios.post(`doctors/doc-upload-image?doctorid=${response.data.doctorid}`, formData, {
              headers: { "Content-Type": "multipart/form-data" }
            });
          } catch (error) {
            console.error("Profile photo upload failed:", error);
            this.photoUploadFailed = true;
          }
        }

        this.gaCompleted = true;
        trackEvent("doctor_registration_success", {
          form_location: this.$route.path,
          attempts: this.gaSubmitAttempts
        });

        this.submittedName = doctorPayload.doctorname;
        this.submittedEmail = doctorPayload.email;
        this.submittedSummary = [
          { label: "Mobile", icon: "fas fa-phone-alt", value: doctorPayload.contactnumbers },
          { label: "Specialization", icon: "fas fa-stethoscope", value: doctorPayload.specialization },
          { label: "Registration no.", icon: "fas fa-id-card", value: doctorPayload.registration_info },
          { label: "Clinic", icon: "fas fa-clinic-medical", value: doctorPayload.clincorhospname },
          { label: "City", icon: "fas fa-map-marker-alt", value: doctorPayload.area }
        ].filter(item => item.value);
        this.submitted = true;
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (error) {
        // Someone registered the same email/number between the check and the save.
        if (error?.response?.status === 409) {
          const detail = String(error.response.data?.detail || "");
          if (this.showConflictErrors({ email_taken: /email/i.test(detail), phone_taken: /contact/i.test(detail) })) {
            this.resetRecaptcha();
            return;
          }
        }
        trackEvent("doctor_registration_error", {
          reason: "submit_failed",
          status: error?.response?.status || error?.name || "unknown",
          attempt: this.gaSubmitAttempts
        });
        this.serverError = this.getRegistrationErrorMessage(error);
        this.resetRecaptcha();
      } finally {
        this.submitting = false;
      }
    },
    resetForm() {
      this.doctorDetails = emptyDoctorDetails();
      this.errors = {};
      this.touched = {};
      this.submitAttempted = false;
      this.serverError = "";
      this.isValidPhone = false;
      this.selectedCityOption = null;
      this.manualCityName = "";
      this.manualState = "";
      this.manualCountry = DEFAULT_COUNTRY;
      this.uploadedImageFile = null;
      this.profileImageUrl = "";
      this.photoUploadFailed = false;
      this.showSocial = false;
      this.submitted = false;
      this.gaCompleted = false;
      this.gaStarted = false;
      this.gaAbandonSent = false;
      this.gaSubmitAttempts = 0;
      this.resetRecaptcha();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }
};
</script>

<style scoped>
.dr-register {
  --dr-primary: #20c0f3;
  --dr-primary-dark: #0e82a6;
  --dr-success: #09a894;
  --dr-danger: #dc3545;
  --dr-text: #272b41;
  --dr-muted: #6c757d;
  --dr-border: #e3e8ee;
  max-width: 920px;
  margin: 0 auto;
  padding: 24px 0 48px;
  color: var(--dr-text);
}

/* Header */
.dr-header {
  text-align: center;
  margin-bottom: 24px;
}
.dr-header h2 {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 4px;
}
.dr-header > p {
  color: var(--dr-muted);
  margin-bottom: 16px;
}
.dr-how {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px 24px;
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 0.9rem;
  color: var(--dr-muted);
}
.dr-how li {
  display: flex;
  align-items: center;
  gap: 8px;
}
.dr-how span {
  display: inline-grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #e8f8fe;
  color: var(--dr-primary-dark);
  font-weight: 600;
  font-size: 0.8rem;
}

/* Cards / sections */
.dr-card {
  background: #fff;
  border: 1px solid var(--dr-border);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(16, 24, 40, 0.04);
  padding: 24px;
  margin-bottom: 20px;
}
.dr-section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--dr-border);
}
.dr-section-num {
  display: inline-grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--dr-primary);
  color: #fff;
  font-size: 0.85rem;
}

/* Fields */
.dr-field {
  margin-bottom: 18px;
}
.dr-field label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 6px;
}
.dr-req {
  color: var(--dr-danger);
}
.dr-optional {
  color: var(--dr-muted);
  font-weight: 400;
}
.dr-field .form-control,
.dr-field .form-select {
  border-radius: 8px;
  min-height: 44px;
}
.dr-field .form-control:focus,
.dr-field .form-select:focus {
  border-color: var(--dr-primary);
  box-shadow: 0 0 0 3px rgba(32, 192, 243, 0.15);
}
.dr-field .form-control.is-invalid,
.dr-field .form-select.is-invalid {
  border-color: var(--dr-danger);
  background-image: none;
}
.dr-hint {
  font-size: 0.8rem;
  color: var(--dr-muted);
  margin-top: 5px;
}
.dr-error {
  font-size: 0.8rem;
  color: var(--dr-danger);
  margin-top: 5px;
}
.dr-field-footer {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}
.dr-counter {
  margin-left: auto;
  font-size: 0.75rem;
  color: var(--dr-muted);
  margin-top: 5px;
  white-space: nowrap;
}
.dr-tel {
  padding: 0;
  display: flex;
}
.dr-tel.is-invalid {
  border-color: var(--dr-danger);
}
.dr-ms-invalid :deep(.multiselect__tags) {
  border-color: var(--dr-danger);
}
.dr-field :deep(.multiselect__tags) {
  min-height: 44px;
  border-radius: 8px;
  border-color: #dee2e6;
}
.dr-field :deep(.multiselect__tag) {
  background: var(--dr-primary);
}

/* Location */
.dr-location-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #e8f8fe;
  color: var(--dr-primary-dark);
  font-size: 0.8rem;
}
.dr-manual-city {
  background: #f7fafc;
  border: 1px dashed #c9d6e2;
  border-radius: 10px;
  padding: 16px 16px 0;
  margin-bottom: 18px;
}

/* Photo */
.dr-photo {
  display: flex;
  align-items: center;
  gap: 16px;
}
.dr-photo img {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--dr-border);
  flex-shrink: 0;
}
.dr-field .dr-photo-btn {
  display: inline-block;
  margin-bottom: 0;
  font-weight: 500;
  cursor: pointer;
}

/* Social */
.dr-social {
  border-top: 1px solid var(--dr-border);
  margin-top: 8px;
  padding-top: 16px;
}
.dr-social-toggle {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f7fafc;
  border: 1px solid var(--dr-border);
  border-radius: 8px;
  padding: 12px 16px;
  font-weight: 600;
  color: var(--dr-text);
}
.dr-social-toggle:hover {
  background: #eef4f8;
}
.dr-social .input-group-text {
  width: 44px;
  justify-content: center;
  background: #f7fafc;
  border-radius: 8px 0 0 8px;
}
.dr-social .input-group .form-control {
  border-radius: 0 8px 8px 0;
}

/* Submit */
.dr-submit {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.dr-recaptcha {
  margin-bottom: 16px;
}
.dr-submit-btn {
  min-width: 260px;
  border-radius: 8px;
}
.dr-alert {
  width: 100%;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  font-size: 0.9rem;
}
.dr-alert-danger {
  background: #fdf0f1;
  border: 1px solid #f5c2c7;
  color: #842029;
}
.dr-alert ul {
  margin: 8px 0 0;
  padding-left: 20px;
}
.dr-alert a {
  color: #842029;
  font-weight: 600;
  text-decoration: underline;
}

/* Success */
.dr-success {
  background: #fff;
  border: 1px solid var(--dr-border);
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(16, 24, 40, 0.08);
  overflow: hidden;
}
.dr-success-hero {
  text-align: center;
  color: #fff;
  padding: 40px 24px 32px;
  background: linear-gradient(135deg, var(--dr-success) 0%, var(--dr-primary) 100%);
}
.dr-success-hero h2 {
  color: #fff;
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 6px;
}
.dr-success-hero p {
  margin-bottom: 16px;
  opacity: 0.95;
}
.dr-success-icon {
  position: relative;
  display: inline-grid;
  place-items: center;
  width: 76px;
  height: 76px;
  border-radius: 50%;
  background: #fff;
  color: var(--dr-success);
  font-size: 2rem;
  margin-bottom: 16px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);
  animation: dr-pop 0.5s cubic-bezier(0.2, 1.4, 0.4, 1) both;
}
.dr-success-icon::after {
  content: "";
  position: absolute;
  inset: -8px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.6);
  animation: dr-ring 1.6s ease-out 0.4s both;
}
@keyframes dr-pop {
  from { transform: scale(0.3); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
@keyframes dr-ring {
  from { transform: scale(0.9); opacity: 1; }
  to { transform: scale(1.35); opacity: 0; }
}
.dr-status-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.45);
  font-size: 0.85rem;
  font-weight: 600;
}
.dr-success-body {
  padding: 28px 32px 24px;
}
.dr-success-grid {
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 28px;
  margin-bottom: 20px;
}
.dr-success-heading {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--dr-muted);
  margin-bottom: 16px;
}

/* Timeline */
.dr-timeline {
  list-style: none;
  padding: 0;
  margin: 0;
}
.dr-timeline li {
  position: relative;
  display: flex;
  gap: 14px;
  padding-bottom: 22px;
}
.dr-timeline li:last-child {
  padding-bottom: 0;
}
.dr-timeline li:not(:last-child)::before {
  content: "";
  position: absolute;
  left: 17px;
  top: 38px;
  bottom: 4px;
  width: 2px;
  background: var(--dr-border);
}
.dr-timeline li.done:not(:last-child)::before {
  background: var(--dr-success);
}
.dr-timeline-dot {
  flex-shrink: 0;
  display: inline-grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #f1f4f7;
  color: var(--dr-muted);
  font-size: 0.85rem;
}
.dr-timeline li.done .dr-timeline-dot {
  background: var(--dr-success);
  color: #fff;
}
.dr-timeline li.current .dr-timeline-dot {
  background: #e8f8fe;
  color: var(--dr-primary-dark);
  box-shadow: 0 0 0 4px rgba(32, 192, 243, 0.15);
}
.dr-timeline-title {
  font-weight: 600;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}
.dr-timeline p {
  margin: 4px 0 0;
  color: var(--dr-muted);
  font-size: 0.875rem;
}
.dr-tag {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f1f4f7;
  color: var(--dr-muted);
}
.dr-tag.done {
  background: #e6f7f4;
  color: #067a6b;
}
.dr-tag.current {
  background: #e8f8fe;
  color: var(--dr-primary-dark);
}

/* Submission summary */
.dr-summary {
  background: #f7fafc;
  border: 1px solid var(--dr-border);
  border-radius: 12px;
  padding: 18px 20px;
  align-self: start;
}
.dr-summary dl {
  margin: 0;
}
.dr-summary dl > div {
  padding: 8px 0;
  border-bottom: 1px dashed var(--dr-border);
}
.dr-summary dl > div:last-child {
  border-bottom: 0;
}
.dr-summary dt {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--dr-muted);
}
.dr-summary dt i {
  width: 16px;
  color: var(--dr-primary);
}
.dr-summary dd {
  margin: 2px 0 0 20px;
  font-weight: 600;
  word-break: break-word;
}

/* Notes / actions */
.dr-note {
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 0.875rem;
  margin-bottom: 14px;
}
.dr-note i {
  margin-right: 6px;
}
.dr-note-warning {
  background: #fff8e6;
  border: 1px solid #ffe2a3;
}
.dr-note-info {
  background: #eef9fd;
  border: 1px solid #cdeefb;
  color: #2a5566;
}
.dr-success-actions {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 20px;
}
.dr-success-actions .btn {
  min-width: 200px;
  border-radius: 8px;
  padding: 10px 20px;
}
.dr-help {
  text-align: center;
  font-size: 0.85rem;
  color: var(--dr-muted);
  margin: 16px 0 0;
}

@media (max-width: 768px) {
  .dr-success-grid {
    grid-template-columns: 1fr;
  }
  .dr-success-body {
    padding: 24px 18px 20px;
  }
  .dr-success-actions .btn {
    width: 100%;
  }
}
@media (prefers-reduced-motion: reduce) {
  .dr-success-icon,
  .dr-success-icon::after {
    animation: none;
  }
}

@media (max-width: 576px) {
  .dr-card {
    padding: 18px 16px;
  }
  .dr-header h2 {
    font-size: 1.4rem;
  }
  .dr-submit-btn {
    width: 100%;
    min-width: 0;
  }
}
</style>
